import { Component } from 'react'

/**
 * Catches a render crash and shows it, instead of a white screen.
 *
 * **Why this exists.** The Setup page read `/admin/users` expecting a bare array; the endpoint returns a page
 * wrapper, so `.filter()` was called on an object, threw, and React unmounted the entire application. The user
 * saw a blank white page — no message, no indication of which page failed, nothing to report beyond "it goes
 * white". A single wrong property name took down the whole console.
 *
 * That the underlying bug is now fixed is not the point. Every page here reads a server response, and a shape
 * that shifts under any of them should cost that page, not the app. This is the web equivalent of CLAUDE.md
 * rule 19 — every state renders, including the one where our own code is wrong.
 *
 * Keyed on the route by the caller, so navigating away from a broken page recovers without a reload. Without
 * that, a boundary latches: the user clicks a working link and still sees the error, which is worse than the
 * white screen because it looks like the whole console is broken rather than one page.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Kept in the console for a screenshot: this is an internal plant tool behind a sign-in, and a readable
    // stack from a supervisor's screen is worth more than hiding it. Source maps ship with the bundle.
    console.error('Console render error:', error, info?.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="state state-error">
        <svg viewBox="0 0 24 24" className="state-icon" aria-hidden="true">
          <path fill="currentColor" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
        <h3>This page could not be displayed</h3>
        <p className="muted">
          Something in the console failed while drawing this screen. The rest of the console still works — use
          the navigation to move elsewhere.
        </p>
        {/* The message verbatim, so a screenshot is actionable. "filter is not a function" told me exactly
            which line to look at; a generic apology would have told me nothing. */}
        <p className="muted"><code>{String(error.message || error)}</code></p>
        <button className="btn" onClick={() => this.setState({ error: null })}>Try again</button>
      </div>
    )
  }
}
